import moment from 'moment'

export const DATE_TIME_FORMAT = 'DD/MM/YYYY hh:mm:ss A'
export const DATE_FORMAT = 'DD/MM/YYYY'
export const TIME_FORMAT = 'hh:mm A'

const PREFORMATTED_DATE = /^\d{2}\/\d{2}\/\d{4}(?: \d{1,2}:\d{2}(?::\d{2})?(?: [AP]M)?)?$/i
const NAIVE_DATE_TIME = /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?$/
const CALENDAR_DATE = /^\d{4}-\d{2}-\d{2}$/
const UTC_SUFFIX = / UTC$/i

/**
 * Mapa de prefijos en español → IANA válidos.
 * Cubre los nombres almacenados en timeZone.json que usan traducciones al español.
 */
const SPANISH_PREFIX_MAP = {
  'América':    'America',
  'Pacífico':   'Pacific',
  'EE.UU.':     'US',
  'EE.UU':      'US',
  'Canadá':     'Canada',
  'México':     'Mexico',
  'Atlántico':  'Atlantic',
  'Europa':     'Europe',
  'África':     'Africa',
  'Ártico':     'Arctic',
  'Índico':     'Indian',
  'Antártica':  'Antarctica',
  'Antártico':  'Antarctica',
  'Antártida':  'Antarctica',
  'Brasil':     'Brazil',
  'Egipto':     'Egypt',
  'Irán':       'Iran',
  'Islandia':   'Iceland',
  'Japón':      'Japan',
  'Libia':      'Libya',
  'Polonia':    'Poland',
  'Singapur':   'Singapore',
  'Turquía':    'Turkey',
  'zulú':       'Zulu',
  'Oeste':      'W-SU',
}

/**
 * Mapa de palabras específicas en español → IANA para subpaths.
 */
const SPANISH_WORD_MAP = {
  'Aleutianas': 'Aleutian',
  'Hawai':      'Hawaii',
  'Oeste':      'West',
}

/**
 * Normaliza un nombre de timezone en español a un nombre IANA válido.
 * Ej: 'América/Caracas' → 'America/Caracas'
 *     'EE.UU./Hawai'    → 'US/Hawaii'
 *
 * @param {string} tz - Nombre de timezone (posiblemente en español)
 * @returns {string} Nombre IANA normalizado
 */
function normalizeTimeZone(tz) {
  if (!tz) return tz

  // Buscar coincidencia de prefijo más larga primero (EE.UU. antes que EE.UU)
  const prefixes = Object.keys(SPANISH_PREFIX_MAP).sort((a, b) => b.length - a.length)
  let normalized = tz

  for (const prefix of prefixes) {
    if (normalized.startsWith(prefix + '/') || normalized === prefix) {
      normalized = SPANISH_PREFIX_MAP[prefix] + normalized.slice(prefix.length)
      break
    }
  }

  // Reemplazar palabras específicas en el subpath
  const slashIdx = normalized.indexOf('/')
  if (slashIdx !== -1) {
    let subpath = normalized.slice(slashIdx + 1)
    for (const [es, en] of Object.entries(SPANISH_WORD_MAP)) {
      subpath = subpath.replace(es, en)
    }
    normalized = normalized.slice(0, slashIdx + 1) + subpath
  }

  // Quitar acentos restantes (ej: Tahití → Tahiti, Cancún → Cancun, Mérida → Merida)
  normalized = normalized.normalize('NFD').replace(/[\u0300-\u036f]/g, '')

  return normalized
}

/**
 * Calcula el offset en minutos para una zona horaria IANA en una fecha específica.
 * Usa la misma convención que getTimezoneOffset(): positivo = detrás de UTC.
 * Soporta nombres en español (los normaliza automáticamente).
 *
 * @param {Date} dateObj - Fecha para la cual calcular el offset (DST-aware)
 * @param {string} ianaTimeZone - Nombre IANA o nombre en español (ej: 'América/Caracas')
 * @returns {number} Offset en minutos (ej: UTC-4 → 240)
 */
function getOffsetMinutesForTimeZone(dateObj, ianaTimeZone) {
  try {
    const tz = normalizeTimeZone(ianaTimeZone)
    const utcStr = dateObj.toLocaleString('en-US', { timeZone: 'UTC' })
    const tzStr  = dateObj.toLocaleString('en-US', { timeZone: tz })
    return (new Date(utcStr).getTime() - new Date(tzStr).getTime()) / 60000
  } catch {
    return dateObj.getTimezoneOffset()
  }
}

export function formatDateInTimeZone(
  value,
  ianaTimeZone,
  format = DATE_TIME_FORMAT,
  { naive = 'utc' } = {}
) {
  if (value === null || value === undefined || value === '') return '-'

  let date
  if (typeof value === 'string') {
    const hasUtcSuffix = UTC_SUFFIX.test(value.trim())
    const text = value.trim().replace(UTC_SUFFIX, '')

    if (PREFORMATTED_DATE.test(text)) return text

    if (CALENDAR_DATE.test(text)) {
      const day = moment(text, 'YYYY-MM-DD', true)
      return day.isValid() ? day.format(DATE_FORMAT) : text
    }

    if (NAIVE_DATE_TIME.test(text)) {
      if (naive === 'wall' && !hasUtcSuffix) {
        const wall = moment(text, moment.ISO_8601, true)
        return wall.isValid() ? wall.format(format) : text
      }
      date = moment.utc(text, moment.ISO_8601, true)
    } else {
      date = moment(text, moment.ISO_8601, true)
      if (!date.isValid()) date = moment(new Date(text))
    }
  } else {
    date = moment(value)
  }

  if (!date.isValid()) return String(value)

  const offsetMin = ianaTimeZone
    ? getOffsetMinutesForTimeZone(date.toDate(), ianaTimeZone)
    : date.toDate().getTimezoneOffset()

  return date.clone().utcOffset(-offsetMin).format(format)
}

/**
 * Convierte un string de fecha a ISO 8601 con el offset de timezone.
 *
 * Si se proporciona ianaTimeZone, usa esa zona horaria para calcular el offset.
 * Si no, usa la zona horaria del navegador (comportamiento original).
 *
 * Formatos de entrada soportados:
 *   - 'YYYY-MM-DD'              (flatpickr sin hora)
 *   - 'YYYY-MM-DD HH:mm'       (flatpickr con hora)
 *   - 'YYYY-MM-DD HH:mm:ss'    (moment format)
 *   - 'YYYY-MM-DD:HH:mm'       (flatpickr variante con ':')
 *
 * Si el input incluye hora, se preserva esa hora.
 * Si el input es solo fecha (YYYY-MM-DD), se usa edge para determinar la hora:
 *   - 'start' → 00:00:00.000
 *   - 'end'   → 23:59:59.999
 *
 * Ejemplo (timezone 'America/Bogota' = UTC-5):
 *   dateToISOWithOffset('2026-03-16', 'start', 'America/Bogota')          → '2026-03-16T00:00:00.000-05:00'
 *   dateToISOWithOffset('2026-03-16', 'end', 'America/Bogota')            → '2026-03-16T23:59:59.999-05:00'
 *   dateToISOWithOffset('2026-03-16 20:00', 'start', 'America/Bogota')    → '2026-03-16T20:00:00.000-05:00'
 *
 * @param {string} dateStr  - Fecha en formato 'YYYY-MM-DD' o 'YYYY-MM-DD HH:mm:ss'
 * @param {'start'|'end'} edge - 'start' → 00:00:00.000, 'end' → 23:59:59.999 (solo cuando no hay hora en el input)
 * @param {string|null} [ianaTimeZone=null] - Zona horaria IANA (ej: 'America/Caracas'). Si es null, usa la del navegador.
 * @returns {string} ISO con offset, o el string original si el formato no es reconocido
 */
export function dateToISOWithOffset(dateStr, edge = 'start', ianaTimeZone = null) {
  if (!dateStr) return dateStr
  if (dateStr instanceof Date) {
    const y = dateStr.getFullYear()
    const m = String(dateStr.getMonth() + 1).padStart(2, '0')
    const d = String(dateStr.getDate()).padStart(2, '0')
    const hh = String(dateStr.getHours()).padStart(2, '0')
    const mm = String(dateStr.getMinutes()).padStart(2, '0')
    const ss = String(dateStr.getSeconds()).padStart(2, '0')
    dateStr = `${y}-${m}-${d} ${hh}:${mm}:${ss}`
  } else if (typeof dateStr !== 'string') {
    return null
  }

  // Extraer la parte YYYY-MM-DD
  const dateOnly = dateStr.slice(0, 10)
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) return dateStr

  // Extraer hora del input si existe (soporta " HH:mm", " HH:mm:ss", ":HH:mm")
  const timePart = dateStr.slice(10).trim()
  let time

  if (timePart) {
    // Remover separador inicial si es ':' (formato "YYYY-MM-DD:HH:mm")
    const cleaned = timePart.startsWith(':') ? timePart.slice(1) : timePart
    // Parsear HH:mm o HH:mm:ss
    const match = cleaned.match(/^(\d{2}):(\d{2})(?::(\d{2}))?/)
    if (match) {
      const hh = match[1]
      const mm = match[2]
      const ss = match[3] || '00'
      time = `${hh}:${mm}:${ss}.000`
    } else {
      time = edge === 'end' ? '23:59:59.999' : '00:00:00.000'
    }
  } else {
    time = edge === 'end' ? '23:59:59.999' : '00:00:00.000'
  }

  // new Date('YYYY-MM-DDTHH:mm:ss.sss') sin Z ni offset → se interpreta como hora LOCAL
  const localDate = new Date(`${dateOnly}T${time}`)
  if (isNaN(localDate.getTime())) return dateStr

  const offsetMin = ianaTimeZone
    ? getOffsetMinutesForTimeZone(localDate, ianaTimeZone)
    : localDate.getTimezoneOffset()

  // getTimezoneOffset() devuelve positivo si estamos detrás de UTC (ej: UTC-3 → 180)
  // ISO 8601 usa el signo inverso: UTC-3 → "-03:00"
  const sign = offsetMin > 0 ? '-' : '+'
  const abs = Math.abs(offsetMin)
  const h = String(Math.floor(abs / 60)).padStart(2, '0')
  const m = String(abs % 60).padStart(2, '0')

  return `${dateOnly}T${time}${sign}${h}:${m}`
}
